import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../data-model/filterValues";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { generateFilterPayloadForMedicalPaOrders } from "../utils/generateFilterPayloadForMedicalPaOrders";
import { useDefaultFacetFiltersForRoute } from "./useDefaultFacetFiltersForRoute";
import { useFilterPayload } from "./useFilterPayload";
import { MedicalPaOrdersState } from "./useMedicalPaOrdersTableData";

/**
 * Handles infinite-scroll pagination by automatically fetching the next page of orders
 * when the user scrolls to the end of the current data set. Computes whether more data
 * is available and appends fetched results to existing state.
 *
 * Sort handling: reads `sortByField` / `sortAscending` from `tablesContext` and threads
 * them through both the primary `filterPayload` (via `useFilterPayload`) and the fallback
 * payload generated when `filterPayload` is still null. This keeps next-page fetches
 * aligned with the active server-side sort.
 *
 * @param validFilters - Currently active filter values
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @param searchTerm - The current free-text search query
 * @param medicalPaOrdersState - Current orders state (used to determine page/total_pages)
 * @param setMedicalPaOrdersState - State setter for orders loading/data lifecycle
 * @param setTableData - State setter for table row data
 * @returns endIndex and its setter, used by the table to signal the visible data boundary
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  medicalPaOrdersState: MedicalPaOrdersState,
  setMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext(
    TableNames.MEDICAL_PA_ORDERS_NEW,
  );
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;
  const defaultFacetFilters = useDefaultFacetFiltersForRoute();
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setMedicalPaOrdersStateRef = useRef(setMedicalPaOrdersState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setMedicalPaOrdersStateRef.current = setMedicalPaOrdersState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setMedicalPaOrdersState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (medicalPaOrdersState.data?.hits?.length ?? 0) - 1 &&
      (medicalPaOrdersState.data?.page ?? 0) <
        (medicalPaOrdersState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    medicalPaOrdersState.data?.hits?.length,
    medicalPaOrdersState.data?.page,
    medicalPaOrdersState.data?.total_pages,
  ]);

  const orgId = getOrgIdForFetchExternalWorklist();

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return generateFilterPayloadForMedicalPaOrders(
        validFilters,
        searchTerm,
        orgId,
        sortByField,
        sortAscending,
        defaultFacetFilters,
      );
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    searchTerm,
    orgId,
    sortByField,
    sortAscending,
    defaultFacetFilters,
  ]);

  const payloadFinalWithPage =
    useMemo<SearchForMedicalPaOrdersFromPostgresParams>(
      () => ({
        ...payloadFinal,
        page: (medicalPaOrdersState.data?.page ?? 0) + 1,
      }),
      [payloadFinal, medicalPaOrdersState.data?.page],
    );

  const currentPage = medicalPaOrdersState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      authStatusOptions as AuthStatusOptionModel[],
      setMedicalPaOrdersStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.MEDICAL_PA_ORDERS_NEW, {
      lastBatchFetched: currentPage + 1,
    });
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
